import { createArcadePrizes } from './188-arcade-prizes.mjs';
import { presentTrinketFeedback } from './59-trinket-feedback.mjs';
import { uiText } from '../04-i18n.mjs';

/** Resolve accepted cell rewards to the visible source region in the overlay's
 * CSS pixels. Whole-board completion without local event cells stays global;
 * missing/offscreen geometry never invents an object position. */
export function gameRewardSource(board, view, state) {
  const events = view.events || [],
    milestone = events.find((event) => event.type === 'milestone');
  const earned = events.flatMap((event) => event.earned || []).map(([index]) => index);
  let cells = earned.length
    ? earned
    : milestone &&
      view.cells?.flatMap((cell, index) => (cell.value === milestone.target ? [index] : []));
  if (!cells?.length)
    cells = [...events]
      .reverse()
      .find(
        (event) =>
          ['find', 'match', 'place', 'clear', 'connect', 'slide'].includes(event.type) &&
          event.cells?.length,
      )?.cells;
  if (!cells && state.gameId === 'spot-pair' && (state.completed || view.stageComplete)) {
    const selected = view.cells?.[state.data?.selected];
    if (Number.isInteger(selected?.value) && selected.value >= 0)
      cells = view.cells.flatMap((cell, index) => (cell.value === selected.value ? [index] : []));
  }
  const origin = board?.parentElement?.getBoundingClientRect?.();
  if (!cells?.length || !origin?.width || !origin.height) return null;
  const bounds = [...new Set(cells)]
    .filter(Number.isInteger)
    .map((index) => board.querySelector?.(`[data-cell="${index}"]`)?.getBoundingClientRect?.())
    .filter((rect) => rect?.width > 0 && rect.height > 0);
  if (!bounds.length) return null;
  return {
    x:
      bounds.reduce((sum, rect) => sum + rect.left + rect.width / 2, 0) / bounds.length -
      origin.left,
    y:
      bounds.reduce((sum, rect) => sum + rect.top + rect.height / 2, 0) / bounds.length -
      origin.top,
  };
}

/** Earned rewards have a hierarchy: rare power combinations and milestones get
 * a short title; ordinary clears stay local. No celebration changes input,
 * score, timer or consent. The caller cancels all returned animations. */
export function presentGameReward(
  ctx,
  { game, state, view, earned, board, feedback, celebration },
  register,
) {
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const parentRegister = register,
    owned = new Set();
  let listening = false,
    closed = false,
    expiry;
  function closeMotion() {
    if (closed) return;
    closed = true;
    globalThis.clearTimeout(expiry);
    media.removeEventListener?.('change', preferenceChanged);
    for (const resource of owned) resource.cancel?.();
    owned.clear();
    celebration.hidden = true;
  }
  function preferenceChanged() {
    if (media.matches) closeMotion();
  }
  register = (resource) => {
    parentRegister(resource);
    if (media.matches || closed) return;
    owned.add(resource);
    if (!listening) {
      listening = true;
      media.addEventListener?.('change', preferenceChanged);
      parentRegister({ cancel: closeMotion });
      // All reward effects finish within1050ms. Bound listener lifetime even
      // where optional animation APIs provide no finished promise.
      expiry = globalThis.setTimeout(closeMotion, 1100);
    }
  };
  const nextLevel = view.stageComplete === true;
  const chain = Math.max(view.combo || 0, view.chain || 0);
  const events = view.events || [];
  const refill = events.find((event) => event.type === 'shuffle');
  if (game.id === 'gem-swap') {
    if (refill)
      feedback.textContent = `${earned > 0 ? `+${earned} · ` : ''}${uiText(refill.reason.startsWith('no swaps') ? 'No legal moves left · Board refilled · Earned powers kept' : 'Fresh board ready · Earned powers kept')}`;
    else if (events.some((event) => event.type === 'invalid'))
      feedback.textContent = uiText('Try another swap · Match three in a row');
  }
  const milestone = events
    .filter(
      (event) => event.type === 'milestone' && Number.isInteger(event.target) && event.target > 0,
    )
    .at(-1);
  const combination = events.find((event) => event.combination)?.combination;
  const powers = events.flatMap((event) => event.earned || []);
  const spectrum = powers.some(([, kind]) => kind === 'spectrum');
  const nova = powers.some(([, kind]) => kind === 'nova');
  const reward = state.completed
    ? 'complete'
    : nextLevel
      ? 'stage'
      : combination
        ? 'power'
        : chain > 1
          ? 'chain'
          : 'clear';
  feedback.dataset.reward = reward;
  if (game.id === 'spot-pair' && board)
    presentTrinketFeedback(
      {
        board,
        view,
        state,
        reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      },
      register,
    );
  if (game.id === 'number-trail' && events.some((event) => event.type === 'miss')) {
    const nextNumber = (view.cells || []).filter((cell) => cell.selected).length + 1;
    feedback.textContent = `Find ${nextNumber} · Your trail is kept`;
  }
  if (game.id === 'spot-pair' && events.some((event) => event.type === 'miss'))
    feedback.textContent = 'Those two do not match · Try another pair';
  if (earned <= 0 && !state.completed && !nextLevel) return;
  if (game.id === 'block-fit' && earned > 0) {
    const clear = events.find((event) => event.type === 'clear');
    feedback.textContent = clear
      ? `+${earned.toLocaleString()} · ${clear.lines} ${clear.lines === 1 ? 'line' : 'lines'}${clear.combo > 1 ? ` · Combo ×${clear.combo}` : ''}`
      : `+${earned.toLocaleString()} · Perfect fit`;
  }
  if (game.id === 'cluster-pop' && earned > 0) {
    const clear = events.find((event) => event.type === 'clear');
    if (clear?.cells?.length)
      feedback.textContent = `+${earned.toLocaleString()} · ${clear.cells.length} bubbles${chain > 1 ? ` · Combo ${chain}` : ''}`;
  }
  if (milestone) feedback.textContent = `+${earned} · ${milestone.target} reached!`;
  else if (combination && !refill)
    feedback.textContent = `+${earned} · ${combination === 'spectrum-spectrum' ? 'Orchard festival' : 'Bloom party'}`;
  else if ((spectrum || nova) && !refill)
    feedback.textContent = `+${earned} · ${spectrum ? 'Harvest' : 'Bloom'} earned!`;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Board-specific gem/bubble/merge effects already carry their own points.
  // Other scored actions get a short, cancellable award inside the play scene.
  if (
    board?.parentElement &&
    earned > 0 &&
    !['gem-swap', 'cluster-pop', 'soft-merge'].includes(game.id) &&
    (game.id !== 'block-fit' || !events.some((event) => event.type === 'clear'))
  ) {
    const toast = ctx.e('span', 'game-earned-toast', `+${earned.toLocaleString()}`);
    toast.setAttribute('aria-hidden', 'true');
    const source = gameRewardSource(board, view, state);
    if (source) {
      toast.style.left = `${source.x}px`;
      toast.style.top = `${source.y}px`;
      toast.dataset.source = 'cells';
    }
    board.parentElement.append(toast);
    let arrival, timer;
    const remove = () => {
      if (timer) window.clearTimeout(timer);
      toast.remove();
    };
    if (!reduced && typeof toast.animate === 'function') {
      arrival = toast.animate(
        [
          { transform: 'translate(-50%, 8px) scale(.85)', opacity: 0 },
          { transform: 'translate(-50%, 0) scale(1)', opacity: 1, offset: 0.18 },
          { transform: 'translate(-50%, -8px) scale(1)', opacity: 1, offset: 0.7 },
          { transform: 'translate(-50%, -24px) scale(1.02)', opacity: 0 },
        ],
        { duration: 950, easing: 'ease-out', fill: 'forwards' },
      );
      void arrival.finished?.then(remove, remove);
    } else {
      toast.style.transform = 'translateX(-50%)';
      timer = window.setTimeout(remove, 950);
    }
    register({
      cancel: () => {
        arrival?.cancel();
        remove();
      },
    });
  }
  const animate = (node, frames, duration) => {
    if (!reduced && typeof node.animate === 'function')
      register(
        node.animate(frames, {
          duration,
          fill: 'forwards',
          easing: 'cubic-bezier(.2,.8,.2,1)',
        }),
      );
  };
  animate(
    feedback,
    [
      { transform: 'translateY(6px)', opacity: 0 },
      { transform: 'translateY(0)', opacity: 1 },
    ],
    240,
  );
  const restrained = [
    'soft-merge',
    'block-fit',
    'colour-path',
    'memory-pairs',
    'tile-slide',
    'rotation-loop',
    'number-trail',
    'spot-pair',
  ].includes(game.id);
  if (
    !state.completed &&
    !nextLevel &&
    (restrained || (!milestone && !combination && !spectrum && !nova && chain < 2))
  )
    return;
  const bubbleClear = game.id === 'cluster-pop' && events.find((event) => event.type === 'clear');
  const label =
    bubbleClear && !state.completed && !nextLevel
      ? bubbleClear.cells.length >= 16
        ? 'SUPER BLOOM!'
        : bubbleClear.cells.length >= 10
          ? 'MEGA POP!'
          : chain >= 5
            ? 'MAX COMBO!'
            : `COMBO ${chain}!`
      : milestone
        ? `${milestone.target} REACHED!`
        : state.completed
          ? ['stalled', 'move-limit'].includes(view.completionReason)
            ? 'ROUND FINISHED!'
            : 'ROUND CLEAR!'
          : combination === 'spectrum-spectrum'
            ? 'ORCHARD FESTIVAL!'
            : combination
              ? 'BLOOM PARTY!'
              : spectrum
                ? 'HARVEST!'
                : nova
                  ? 'BLOOM!'
                  : nextLevel
                    ? `LEVEL ${view.stage} CLEAR!`
                    : `${chain} ${view.chain > view.combo ? 'CASCADES' : 'CHAIN'}!`;
  celebration.replaceChildren(ctx.e('strong', '', label));
  if (game.id === 'gem-swap') {
    const rendered = ctx.e('span', 'game-rendered-celebration');
    rendered.setAttribute?.('aria-hidden', 'true');
    celebration.append(rendered);
  }
  if (game.id === 'cluster-pop' && (state.completed || nextLevel || chain >= 3)) {
    const rendered = ctx.e('span', 'bubble-reward-sprite');
    rendered.setAttribute('aria-hidden', 'true');
    celebration.append(rendered);
  }
  const source = !state.completed && !nextLevel && gameRewardSource(board, view, state);
  if (celebration.style) {
    Object.assign(celebration.style, { left: '', right: '', top: '', width: '' });
    if (source) {
      const bounds = board.parentElement.getBoundingClientRect(),
        width = Math.min(240, bounds.width - 16);
      Object.assign(celebration.style, {
        left: `${Math.max(8, Math.min(bounds.width - width - 8, source.x - width / 2))}px`,
        right: 'auto',
        top: `${Math.max(8, Math.min(bounds.height - 48, source.y - 24))}px`,
        width: `${width}px`,
      });
    }
  }
  celebration.hidden = false;
  if (reduced) {
    const timer = globalThis.setTimeout(() => {
      celebration.hidden = true;
    }, 1050);
    register({
      cancel() {
        globalThis.clearTimeout(timer);
        celebration.hidden = true;
      },
    });
  }
  if (board?.parentElement) {
    const prizes = createArcadePrizes(ctx, board.parentElement);
    register({ cancel: () => prizes.dispose() });
    prizes.play({ finale: state.completed || nextLevel });
  }
  animate(
    celebration,
    [
      { opacity: 1, transform: 'translateY(0)' },
      { opacity: 1, offset: 0.65 },
      { opacity: 0, transform: 'translateY(-8px)' },
    ],
    1050,
  );
}
